'use client';

import React, { useEffect } from 'react';
import Image from 'next/image';
import Link from 'next/link';

const useScrollReveal = () => {
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) entry.target.classList.add('visible');
        });
      },
      { threshold: 0.08, rootMargin: '0px 0px -40px 0px' }
    );
    const elements = document.querySelectorAll('.fade-in-up');
    elements?.forEach((el) => observer?.observe(el));
    return () => observer?.disconnect();
  }, []);
};

const timelineSteps = [
  { num: '01', title: 'Receive your university offer letter', desc: 'Your journey begins with an official acceptance from your chosen Spanish or Portuguese university.' },
  { num: '02', title: 'Our consultants prepare your full document pack', desc: 'We compile every required document — financial proof, insurance, accommodation, criminal record certificate, and more.' },
  { num: '03', title: 'Book your consulate appointment', desc: 'We guide you through the VFS Global or consulate booking process in your home country.' },
  { num: '04', title: 'Submit your visa application', desc: 'Your complete application is submitted with confidence — nothing missing, nothing delayed.' },
  { num: '05', title: 'Receive your visa approval', desc: 'Processing typically takes 4–8 weeks. We track your application and keep you informed throughout.' },
  { num: '06', title: 'Travel to Spain or Portugal', desc: 'Your consultant briefs you on arrival procedures, what to expect at the airport, and your first steps.' },
  { num: '07', title: 'Register on arrival — NIF/NIE, housing, health', desc: 'We handle your local registration, tax number, health centre, and full city orientation from day one.' },
  { num: '08', title: 'Begin your studies', desc: 'You arrive prepared, registered, and ready to focus on what matters — your education.' },
  { num: '09', title: 'Activate your post-study work permit', desc: 'After graduation, we guide you through the 2-year post-study work authorisation process.' },
];

export default function VisaImmigrationPage() {
  useScrollReveal();

  return (
    <div className="bg-white">
      {/* HERO */}
      <section className="relative h-[60vh] min-h-[480px] flex items-end overflow-hidden">
        <div className="absolute inset-0 z-0">
          <Image
            src="/assets/images/eva-darron-oCdVtGFeDC0-unsplash-1779274381048.jpg"
            alt="Airplane window view of a stunning sunset over the clouds during flight"
            fill
            className="object-cover object-center"
            priority
            quality={90}
          />
          <div className="absolute inset-0 city-overlay" />
        </div>
        <div className="relative z-10 max-w-5xl mx-auto px-6 pb-16 pt-32">
          <p className="eyebrow text-white/80 mb-4">Visa &amp; Immigration</p>
          <h1 className="font-headline font-extrabold text-white" style={{ textShadow: '0 2px 20px rgba(0,0,0,0.3)' }}>
            We Handle the Visa —<br />You Focus on Your Future
          </h1>
        </div>
      </section>
      {/* SUBHEADLINE */}
      <section className="py-16 bg-white">
        <div className="max-w-4xl mx-auto px-6 fade-in-up">
          <span className="accent-bar" />
          <p className="font-body leading-relaxed" style={{ fontSize: '1.2rem', color: '#3d5566' }}>
            Our immigration consultants guide you through every step of the Spanish and Portuguese student visa process — so nothing gets missed and nothing gets delayed.
          </p>
        </div>
      </section>
      {/* VISA REQUIREMENTS */}
      <section className="py-16" style={{ backgroundColor: '#F5EDD6' }}>
        <div className="max-w-6xl mx-auto px-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-10">
            {/* Portugal */}
            <div className="fade-in-up bg-white rounded-2xl p-8 border" style={{ borderColor: '#e0d8c8' }}>
              <div className="flex items-center gap-3 mb-6">
                <span className="text-3xl">🇵🇹</span>
                <div>
                  <h2 className="font-headline font-bold" style={{ fontSize: '1.6rem', color: '#1a2e3b' }}>
                    Portugal
                  </h2>
                  <p className="font-body text-sm" style={{ color: '#5a6e7a' }}>Student Visa (Type D)</p>
                </div>
              </div>
              <h4 className="font-body font-semibold text-sm uppercase tracking-wider mb-4" style={{ color: '#2A7FA5' }}>Requirements</h4>
              <ul className="space-y-2 mb-6">
                {[
                  'University acceptance letter',
                  'Proof of financial means (~€760/month)',
                  'Valid passport',
                  'Health insurance',
                  'Accommodation proof',
                  'Clean criminal record certificate',
                ]?.map((req, i) => (
                  <li key={i} className="flex items-start gap-2 font-body text-sm" style={{ color: '#3d5566' }}>
                    <svg className="w-4 h-4 mt-0.5 flex-shrink-0" style={{ color: '#2A7FA5' }} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                    </svg>
                    {req}
                  </li>
                ))}
              </ul>
              <div className="rounded-xl p-4 mb-4" style={{ backgroundColor: '#e8f4f9' }}>
                <p className="font-body text-sm font-semibold mb-1" style={{ color: '#1a2e3b' }}>Timeline</p>
                <p className="font-body text-sm" style={{ color: '#3d5566' }}>Apply via VFS Global or Portuguese Consulate. Processing: 4–8 weeks</p>
              </div>
              <div className="rounded-xl p-4 mb-4" style={{ backgroundColor: '#fdfaf5', border: '1px solid #e0d8c8' }}>
                <p className="font-body text-sm font-semibold mb-1" style={{ color: '#1a2e3b' }}>Post-Arrival</p>
                <p className="font-body text-sm" style={{ color: '#3d5566' }}>AIMA registration · NIF (tax number) · Local health centre registration</p>
              </div>
              <div className="rounded-xl p-4" style={{ backgroundColor: '#F5EDD6' }}>
                <p className="font-body text-sm font-semibold mb-1" style={{ color: '#1a2e3b' }}>Post-Study</p>
                <p className="font-body text-sm" style={{ color: '#3d5566' }}>2-year job-seeker permit. Permanent residency pathway after 5 years.</p>
              </div>
            </div>

            {/* Spain */}
            <div className="fade-in-up bg-white rounded-2xl p-8 border" style={{ borderColor: '#e0d8c8', transitionDelay: '0.12s' }}>
              <div className="flex items-center gap-3 mb-6">
                <span className="text-3xl">🇪🇸</span>
                <div>
                  <h2 className="font-headline font-bold" style={{ fontSize: '1.6rem', color: '#1a2e3b' }}>
                    Spain
                  </h2>
                  <p className="font-body text-sm" style={{ color: '#5a6e7a' }}>Student Visa (Type D)</p>
                </div>
              </div>
              <h4 className="font-body font-semibold text-sm uppercase tracking-wider mb-4" style={{ color: '#2A7FA5' }}>Requirements</h4>
              <ul className="space-y-2 mb-6">
                {[
                  'University enrolment letter',
                  'Proof of funds (~€600/month)',
                  'Valid passport',
                  'Health insurance with Spanish coverage',
                  'Accommodation proof',
                  'Clean criminal record',
                ]?.map((req, i) => (
                  <li key={i} className="flex items-start gap-2 font-body text-sm" style={{ color: '#3d5566' }}>
                    <svg className="w-4 h-4 mt-0.5 flex-shrink-0" style={{ color: '#2A7FA5' }} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                    </svg>
                    {req}
                  </li>
                ))}
              </ul>
              <div className="rounded-xl p-4 mb-4" style={{ backgroundColor: '#e8f4f9' }}>
                <p className="font-body text-sm font-semibold mb-1" style={{ color: '#1a2e3b' }}>Timeline</p>
                <p className="font-body text-sm" style={{ color: '#3d5566' }}>Apply at Spanish Consulate in home country. Processing: 4–6 weeks</p>
              </div>
              <div className="rounded-xl p-4" style={{ backgroundColor: '#F5EDD6' }}>
                <p className="font-body text-sm font-semibold mb-1" style={{ color: '#1a2e3b' }}>Post-Study</p>
                <p className="font-body text-sm" style={{ color: '#3d5566' }}>2-year post-study work authorisation. EU residency pathway after 5 years.</p>
              </div>
            </div>
          </div>
        </div>
      </section>
      {/* STEP-BY-STEP TIMELINE */}
      <section className="py-24 bg-white">
        <div className="max-w-5xl mx-auto px-6">
          <div className="text-center mb-16 fade-in-up">
            <span className="eyebrow">The Process</span>
            <h2 className="font-headline font-bold mt-3" style={{ color: '#1a2e3b' }}>
              Your Visa Journey, Step by Step
            </h2>
          </div>
          <div className="space-y-6">
            {timelineSteps?.map((step, i) => (
              <div
                key={i}
                className="fade-in-up flex gap-6 items-start"
                style={{ transitionDelay: `${i * 0.07}s` }}
              >
                <div
                  className="flex-shrink-0 w-12 h-12 rounded-full flex items-center justify-center font-headline font-bold text-white text-sm"
                  style={{ backgroundColor: '#2A7FA5' }}
                >
                  {step?.num}
                </div>
                <div className="flex-1 pb-6 border-b" style={{ borderColor: '#e0d8c8' }}>
                  <h3 className="font-headline font-bold mb-2" style={{ fontSize: '1.15rem', color: '#1a2e3b' }}>
                    {step?.title}
                  </h3>
                  <p className="font-body text-sm leading-relaxed" style={{ color: '#5a6e7a' }}>
                    {step?.desc}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>
      {/* POST-ARRIVAL SUPPORT */}
      <section className="py-20" style={{ backgroundColor: '#F5EDD6' }}>
        <div className="max-w-5xl mx-auto px-6">
          <div className="text-center mb-12 fade-in-up">
            <span className="eyebrow">After You Arrive</span>
            <h2 className="font-headline font-bold mt-3" style={{ color: '#1a2e3b' }}>
              Post-Arrival Support
            </h2>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4 fade-in-up">
            {[
              'NIF/NIE registration',
              'Housing guidance',
              'Health system registration',
              'Bank account setup',
              'City orientation and welcome support',
              'SIM card setup',
            ]?.map((item, i) => (
              <div
                key={i}
                className="bg-white rounded-xl px-5 py-4 flex items-center gap-3 border"
                style={{ borderColor: '#e0d8c8' }}
              >
                <svg className="w-5 h-5 flex-shrink-0" style={{ color: '#2A7FA5' }} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                </svg>
                <span className="font-body text-sm font-medium" style={{ color: '#1a2e3b' }}>{item}</span>
              </div>
            ))}
          </div>
        </div>
      </section>
      {/* CTA */}
      <section className="py-20" style={{ backgroundColor: '#2A7FA5' }}>
        <div className="max-w-3xl mx-auto px-6 text-center fade-in-up">
          <h2 className="font-headline font-bold text-white mb-6" style={{ fontSize: 'clamp(2rem, 4vw, 2.8rem)' }}>
            Ready to Start Your Visa Process?
          </h2>
          <Link
            href="/contact"
            className="inline-flex items-center justify-center px-10 py-4 font-body font-semibold rounded-lg transition-all duration-300 hover:-translate-y-1"
            style={{ backgroundColor: '#F5EDD6', color: '#2A7FA5', fontSize: '1.05rem' }}
          >
            Talk to an Immigration Consultant
          </Link>
        </div>
      </section>
    </div>
  );
}
